/**
 * The photo book.
 *
 * A year of a classroom: the 100th day, the field trip, the tooth that came
 * out during reading. Three things shape it.
 *
 *  1. **The original is kept.** These are the photographs, not thumbnails of
 *     them. She should be able to print one at eight by ten in June and get
 *     back what the camera saw, so the full file is stored and a small copy
 *     is made alongside it for the grid to load from.
 *
 *  2. **A photo belongs to a month.** Which month is decided by when it was
 *     taken, not when it was uploaded — a field trip photographed on Friday
 *     and uploaded the following Wednesday belongs to the trip.
 *
 *  3. **It empties in June, and nothing is lost.** The year is tagged and
 *     put away, exactly as the roster is. Last year's photo book is still
 *     there to look at, print and download; it is simply not this year's.
 */
export const PHOTO_RULES = {
    /**
     * What a phone or a school camera actually produces.
     *
     * SVG is not on the list and never should be: it is a script container that
     * would be served from our own origin, and this origin holds a class list.
     */
    acceptedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'],
    /**
     * 25 MB.
     *
     * Comfortably above a modern phone's 4–8 MB and below the 16 MB a single
     * Mongo document can hold — the original is re-encoded on the way in, which
     * brings a 25 MB raw-ish JPEG down well under the limit while keeping every
     * pixel. Anything larger is a camera raw file, and this is not Lightroom.
     */
    maxUploadBytes: 25 * 1024 * 1024,
    /** After re-encoding. A hard stop, because a document that will not save is worse. */
    maxStoredBytes: 14 * 1024 * 1024,
    /** The grid's copy. Small enough that a hundred of them load at once. */
    thumbPx: 400,
    /** Per teacher, per year. A photo a day for a school year is about 180. */
    maxPerYear: 2000,
    /** How many can be zipped in one go, so a download cannot ask for a gigabyte. */
    maxZipPhotos: 400,
};
/* ---------------------------------------------------------------- months */
/** 'YYYY-MM' from an ISO date-time — the key a photo is grouped under. */
export function photoMonth(takenAt) {
    return takenAt.slice(0, 7);
}
const MONTHS = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
];
export function monthLabel(month) {
    const [y, m] = month.split('-').map(Number);
    return `${MONTHS[(m ?? 1) - 1]} ${y}`;
}
/** 'Friday 14 May, 9:30 am' — under a photo, so the time is there too. */
export function photoTimeLabel(takenAt) {
    const date = new Date(takenAt);
    if (Number.isNaN(date.getTime()))
        return '';
    return date.toLocaleString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}
/**
 * The photos, in months, newest month first and newest photo first within it.
 *
 * The order a teacher looks for a photo in: she remembers roughly when, and
 * the thing she wants is usually the thing she just took.
 */
export function byMonth(photos) {
    const groups = new Map();
    for (const photo of photos) {
        const key = photoMonth(photo.takenAt);
        groups.set(key, [...(groups.get(key) ?? []), photo]);
    }
    return [...groups.entries()]
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([month, list]) => ({
        month,
        photos: [...list].sort((a, b) => b.takenAt.localeCompare(a.takenAt)),
    }));
}
/**
 * A file name for a download.
 *
 * Named after the photo where it has a name, dated where it does not, and
 * scrubbed of anything a file system would object to — a teacher who calls a
 * photo "Field trip: 5/14" should get a file, not an error.
 */
export function photoFileName(photo, index = 0) {
    const extension = photo.contentType === 'image/png' ? 'png' : 'jpg';
    const base = (photo.title.trim() || `Photo ${photo.takenAt.slice(0, 10)}`)
        .replace(/[/\\:*?"<>|]/g, '-')
        .replace(/\s+/g, ' ')
        .slice(0, 80)
        .trim();
    // A suffix only where one is needed: two photos on the same day with no
    // name would otherwise both be 'Photo 2026-05-14.jpg' inside the same zip.
    return index > 0 ? `${base} (${index + 1}).${extension}` : `${base}.${extension}`;
}
/** Every name in a set, made unique, in order. */
export function photoFileNames(photos) {
    const used = new Map();
    return photos.map((photo) => {
        const first = photoFileName(photo);
        const seen = used.get(first) ?? 0;
        used.set(first, seen + 1);
        return seen === 0 ? first : photoFileName(photo, seen);
    });
}
//# sourceMappingURL=photos.js.map